import {
  ClockCircleOutlined,
  MoreOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
  SyncOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  DatePicker,
  Dropdown,
  Empty,
  Input,
  Modal,
  Pagination,
  Select,
  Skeleton,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState, type KeyboardEvent, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "../api/client";
import { fetchProjectMetadata } from "../api/projects";
import {
  fetchAdminReviewTasks,
  fetchReviewerOptions,
  fetchReviewTasks,
  reassignReviewAssignment,
  revokeReviewAssignment,
  type ReviewTaskListItem,
} from "../api/reviews";
import { ProjectFolderIcon } from "../features/projects/ProjectFolderIcon";

const PAGE_SIZE = 10;

interface ReviewTasksPageProps {
  admin?: boolean;
}

interface TaskFilters {
  search: string;
  workTypeCode?: string;
  reviewerUserId?: string;
  assignedFrom?: string;
  assignedTo?: string;
}

function formatDate(value: string) {
  return dayjs(value).format("YYYY-MM-DD");
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

export function ReviewTasksPage({ admin = false }: ReviewTasksPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState("");
  const [workTypeDraft, setWorkTypeDraft] = useState<string>();
  const [reviewerDraft, setReviewerDraft] = useState<string>();
  const [assignedDateDraft, setAssignedDateDraft] = useState<Dayjs | null>(null);
  const [filters, setFilters] = useState<TaskFilters>({ search: "" });
  const [reassignTask, setReassignTask] = useState<ReviewTaskListItem>();
  const [replacementReviewerId, setReplacementReviewerId] = useState<string>();

  const metadataQuery = useQuery({
    queryKey: ["project-metadata"],
    queryFn: ({ signal }) => fetchProjectMetadata(signal),
    staleTime: 30 * 60_000,
  });
  const reviewersQuery = useQuery({
    queryKey: ["reviewer-options"],
    queryFn: () => fetchReviewerOptions(),
    enabled: admin,
    staleTime: 60_000,
  });
  const tasksQuery = useQuery({
    queryKey: [admin ? "admin-review-tasks" : "review-tasks", page, filters],
    queryFn: ({ signal }) =>
      admin
        ? fetchAdminReviewTasks({ page, pageSize: PAGE_SIZE, ...filters }, signal)
        : fetchReviewTasks({ page, pageSize: PAGE_SIZE, ...filters }, signal),
  });
  const revokeMutation = useMutation({
    mutationFn: revokeReviewAssignment,
    onSuccess: async () => {
      message.success("审核任务已撤销");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-review-tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["reviewer-options"] }),
      ]);
    },
    onError: (error) => message.error(errorMessage(error, "撤销审核失败")),
  });
  const reassignMutation = useMutation({
    mutationFn: ({
      assignmentId,
      reviewerUserId,
    }: {
      assignmentId: string;
      reviewerUserId: string;
    }) => reassignReviewAssignment(assignmentId, reviewerUserId),
    onSuccess: async () => {
      message.success("审核任务已重新分配");
      setReassignTask(undefined);
      setReplacementReviewerId(undefined);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-review-tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["reviewer-options"] }),
      ]);
    },
    onError: (error) => message.error(errorMessage(error, "重新分配失败")),
  });

  const workTypeLabels = new Map(
    (metadataQuery.data?.work_types ?? []).map((item) => [item.code, item.label]),
  );
  const applyFilters = () => {
    setFilters({
      search: searchDraft.trim(),
      workTypeCode: workTypeDraft,
      reviewerUserId: admin ? reviewerDraft : undefined,
      assignedFrom: assignedDateDraft?.format("YYYY-MM-DD"),
      assignedTo: assignedDateDraft?.format("YYYY-MM-DD"),
    });
    setPage(1);
  };
  const resetFilters = () => {
    setSearchDraft("");
    setWorkTypeDraft(undefined);
    setReviewerDraft(undefined);
    setAssignedDateDraft(null);
    setFilters({ search: "" });
    setPage(1);
  };
  const openTask = (task: ReviewTaskListItem) => {
    void navigate(admin ? `/admin/projects/${task.project_id}` : `/review-tasks/${task.id}`);
  };
  const handleTaskKeyDown = (event: KeyboardEvent<HTMLElement>, task: ReviewTaskListItem) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openTask(task);
    }
  };
  const stopPropagation = (event: MouseEvent) => event.stopPropagation();
  const confirmRevoke = (task: ReviewTaskListItem) => {
    modal.confirm({
      title: "确认撤销审核？",
      content: `撤销后，${task.reviewer_name} 将无法继续查看“${task.work_name}”。`,
      okText: "确认撤销",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: () => revokeMutation.mutateAsync(task.id),
    });
  };
  const updatedAt = tasksQuery.dataUpdatedAt
    ? new Date(tasksQuery.dataUpdatedAt).toLocaleTimeString("zh-CN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "--:--";
  const tasks = tasksQuery.data?.items ?? [];
  const total = tasksQuery.data?.total ?? 0;
  const reviewerOptions = (reviewersQuery.data ?? []).map((reviewer) => ({
    value: reviewer.id,
    label: `${reviewer.name}（${reviewer.username}）`,
  }));
  const replacementOptions = reviewerOptions.filter(
    (reviewer) => reviewer.value !== reassignTask?.reviewer_user_id,
  );

  return (
    <main className="review-task-page">
      <header className="review-task-page-header">
        <div>
          <Typography.Title level={1}>审核任务</Typography.Title>
          <p>{admin ? "查看并管理全部审核任务" : "查看分配给我的版权登记项目及相关材料"}</p>
        </div>
        <div className="review-task-refresh">
          <Button
            icon={<ReloadOutlined />}
            loading={tasksQuery.isFetching}
            onClick={() => void tasksQuery.refetch()}
          >
            刷新任务
          </Button>
          <span>更新于 {updatedAt}</span>
        </div>
      </header>

      <section className="review-task-library" aria-label="审核任务列表">
        <header className="review-task-library-heading">
          <Typography.Title level={2}>审核任务列表</Typography.Title>
          <span>共 {total} 个任务</span>
        </header>

        <div className={`review-task-filters${admin ? " review-task-filters--admin" : ""}`}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            value={searchDraft}
            placeholder="搜索作品名称或项目编号"
            onChange={(event) => setSearchDraft(event.target.value)}
            onPressEnter={applyFilters}
          />
          <Select
            aria-label="作品类型"
            allowClear
            value={workTypeDraft}
            placeholder="作品类型"
            loading={metadataQuery.isPending}
            options={(metadataQuery.data?.work_types ?? []).map((item) => ({
              value: item.code,
              label: item.label,
            }))}
            onChange={setWorkTypeDraft}
          />
          {admin ? (
            <Select
              aria-label="所属审核员"
              allowClear
              showSearch
              optionFilterProp="label"
              value={reviewerDraft}
              placeholder="所属审核员"
              loading={reviewersQuery.isPending}
              options={reviewerOptions}
              onChange={setReviewerDraft}
            />
          ) : null}
          <DatePicker
            aria-label="分配时间"
            allowClear
            value={assignedDateDraft}
            placeholder="分配时间"
            onChange={setAssignedDateDraft}
          />
          <Button onClick={resetFilters}>重置</Button>
          <Button type="primary" onClick={applyFilters}>
            查询
          </Button>
        </div>

        {tasksQuery.isPending ? (
          <div className="review-task-grid" aria-label="正在加载审核任务">
            {Array.from({ length: 10 }, (_, index) => (
              <Skeleton.Node key={index} active className="review-task-card-skeleton" />
            ))}
          </div>
        ) : null}

        {tasksQuery.isError ? (
          <Alert type="error" showIcon title="审核任务读取失败，请刷新后重试" />
        ) : null}

        {!tasksQuery.isPending && !tasksQuery.isError && !tasks.length ? (
          <Empty description={admin ? "暂无审核任务" : "暂无分配给你的审核任务"} />
        ) : null}

        {tasks.length ? (
          <div className="review-task-grid">
            {tasks.map((task) => (
              <article
                className={`review-task-card${admin ? " review-task-card--admin" : ""}`}
                role="button"
                tabIndex={0}
                aria-label={`查看审核任务：${task.work_name}`}
                key={task.id}
                onClick={() => openTask(task)}
                onKeyDown={(event) => handleTaskKeyDown(event, task)}
              >
                {admin ? (
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        { key: "revoke", label: "撤销审核", icon: <StopOutlined />, danger: true },
                        {
                          key: "reassign",
                          label: "重新分配",
                          icon: <UserSwitchOutlined />,
                        },
                      ],
                      onClick: ({ key, domEvent }) => {
                        domEvent.stopPropagation();
                        if (key === "revoke") confirmRevoke(task);
                        if (key === "reassign") {
                          setReassignTask(task);
                          setReplacementReviewerId(undefined);
                        }
                      },
                    }}
                  >
                    <Button
                      className="review-task-card-menu"
                      type="text"
                      shape="circle"
                      icon={<MoreOutlined />}
                      aria-label={`打开${task.work_name}的审核操作菜单`}
                      onClick={stopPropagation}
                    />
                  </Dropdown>
                ) : null}
                <ProjectFolderIcon className="review-task-folder" />
                <strong title={task.work_name}>{task.work_name}</strong>
                <span className="review-task-work-type">
                  {workTypeLabels.get(task.work_type_code) ?? task.work_type_code}
                </span>
                {admin ? (
                  <span className="review-task-reviewer" title={task.reviewer_name}>
                    审核员：{task.reviewer_name}
                  </span>
                ) : null}
                <dl>
                  <div>
                    <dt>
                      <ClockCircleOutlined /> 分配于
                    </dt>
                    <dd>{formatDate(task.assigned_at)}</dd>
                  </div>
                  <div>
                    <dt>
                      <SyncOutlined /> 最后更新
                    </dt>
                    <dd>{formatDate(task.updated_at)}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        ) : null}

        <footer className="review-task-library-footer">
          <span>共 {total} 个任务</span>
          <Pagination
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            showSizeChanger={false}
            hideOnSinglePage={false}
            onChange={setPage}
          />
        </footer>
      </section>

      <Modal
        title="重新分配审核任务"
        open={Boolean(reassignTask)}
        width={480}
        okText="确认分配"
        cancelText="取消"
        destroyOnHidden
        okButtonProps={{
          disabled: !replacementReviewerId,
          loading: reassignMutation.isPending,
        }}
        onCancel={() => {
          setReassignTask(undefined);
          setReplacementReviewerId(undefined);
        }}
        onOk={() => {
          if (reassignTask && replacementReviewerId) {
            reassignMutation.mutate({
              assignmentId: reassignTask.id,
              reviewerUserId: replacementReviewerId,
            });
          }
        }}
      >
        <div className="review-task-reassign-form">
          <p>
            当前审核员：<strong>{reassignTask?.reviewer_name}</strong>
          </p>
          <label htmlFor="review-task-replacement-reviewer">重新分配给</label>
          <Select
            id="review-task-replacement-reviewer"
            aria-label="重新分配给"
            showSearch
            optionFilterProp="label"
            value={replacementReviewerId}
            placeholder="请选择新的审核员"
            loading={reviewersQuery.isPending}
            options={replacementOptions}
            onChange={setReplacementReviewerId}
          />
        </div>
      </Modal>
    </main>
  );
}
